import {Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {FormArray, FormGroup, UntypedFormControl, Validators} from '@angular/forms';
import {MatAccordion} from '@angular/material/expansion';
import {ActivatedRoute, Router} from '@angular/router';
import {EMPTY, finalize, Observable, of, pipe, switchMap} from 'rxjs';
import {catchError, map, startWith, tap} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Role} from '../models/role';
import {User} from '../models/user';
import {LocationMapperService} from '../services/location-mapper.service';
import {MerchantService} from '../services/merchant.service';
import {UserService} from '../services/user.service';
import {Mode} from '../models/mode';
import {MatDialog} from '@angular/material/dialog';
import {MatChipInputEvent} from '@angular/material/chips';
import {MccCode, MccCodes} from '../models/mcc';
import {Gateway} from '../models/gateway';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Overlay, OverlayRef} from '@angular/cdk/overlay';
import {ComponentPortal} from '@angular/cdk/portal';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {
  formatUtcIsoStringToLocal,
  LocationApplePayPaymentProcessingCertificate,
  LocationConfig,
  Terminal
} from "../models/locationconfig";
import {Status} from "../models/status";
import {AmexAccountType} from "../models/amex-account";

@Component({
  selector: 'app-create-merchant',
  templateUrl: './create-merchant.component.html',
  styleUrls: ['./create-merchant.component.sass'],
  standalone: false
})
export class CreateMerchantComponent implements OnInit {
  gatewayOptions = Object.values(Gateway);
  statuses = Object.values(Status);
  knownCountryCodes = ["GB", "IE", "ES", "PT", "IT", "FR", "DE"];
  requiredFields: Record<Gateway, string[]> = {
    [Gateway.PayVector]: ['gatewayUsername'],
    [Gateway.Cybersource]: ['url', 'merchantCategoryCode'],
    [Gateway.Mars]: ['customerId', 'emailAddress'],
  };

  mode: string;
  user: User;
  formatUtcIsoStringToLocal = formatUtcIsoStringToLocal;

  mccCtrl = new UntypedFormControl();
  mccOptions: MccCode[] = MccCodes;
  filteredMccOptions: Observable<MccCode[]>;

  amexAccountTypes = Object.values(AmexAccountType);

  domainNamesCtrl = new UntypedFormControl();
  locationConfigFG: FormGroup

  registeredDomainNames: string[] = [];
  isProcessing = false;

  spinnerOverlayRef: OverlayRef;

  private destroyRef = inject(DestroyRef);

  @ViewChild(MatAccordion, {static: true}) accordion: MatAccordion;

  constructor(private merchantService: MerchantService,
    private locationMapperService: LocationMapperService,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private overlay: Overlay) {
    this.userService.getUserRole().then((user) => {
      this.user = user;
    });

    if(!this.spinnerOverlayRef) {
      this.spinnerOverlayRef = this.overlay.create({
        hasBackdrop: true,
        backdropClass: 'dark-backdrop',
        positionStrategy: this.overlay.position()
            .global()
            .centerHorizontally()
            .centerVertically()
        });
    }
  }

  ngOnInit() {
    let mid = this.route.snapshot.paramMap.get('mid');
    if (mid) {
      this.mode = Mode.Update;

      if (this.isViewer) {
        this.mode = Mode.View;
        this.domainNamesCtrl.disable();
      }

      if (!this.isAdmin && !this.isMaintainer) {
        this.mccCtrl.disable();
      }

      this.merchantService.getByMID(mid).pipe(
        switchMap((locationConfig) => {
          this.locationConfigFG = this.locationMapperService.createFormModel(locationConfig);
          this.setupLocationConfigConditionals();

          // IDs are read-only in Edit Mode
          this.locationConfigFG.get('id')?.disable()
          this.locationConfigFG.get('customerId')?.disable()

          if (!this.hasApplePay) {
            return EMPTY
          }
          return of(locationConfig)
        }),
        this.refreshRegisteredDomainNames(mid),
        catchError((e) => {
          console.error('error trying to fetch merchant.', e)
          this.router.navigate(['merchants'])
          return EMPTY
        }),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe()
    } else {
      // Check if we have state passed from the Duplicate action
      const stateData = history.state?.data;

      // Initialize form.
      // If stateData exists, it populates the fields.
      this.mode = Mode.Create;
      if (stateData) {
        this.locationConfigFG = this.locationMapperService.createFormModel(stateData);
      } else {
        this.locationConfigFG = this.locationMapperService.createFormModel();
      }

      // ID is editable in Create Mode
      this.locationConfigFG.get('id')?.enable();
      this.locationConfigFG.get('customerId')?.enable();

      this.setupLocationConfigConditionals();
    }

    this.filteredMccOptions = this.mccCtrl.valueChanges
      .pipe(startWith(''), map(value => this.filterMccCode(value)));
  }

  setupLocationConfigConditionals() {
    const gatewayCtrl = this.locationConfigFG.get('gatewayName');
    const amexTypeCtrl = this.locationConfigFG.get('amexAccountType');

    gatewayCtrl.valueChanges.subscribe(gateway => {
      this.updateGatewayValidators(gateway);
      this.updateAmexOptions(gateway);
    });

    amexTypeCtrl.valueChanges.subscribe(type => {
      this.updateAmexValidators(type);
    });

    gatewayCtrl.updateValueAndValidity();
    amexTypeCtrl.updateValueAndValidity();
  }

  updateGatewayValidators(gateway: Gateway) {
    const allManagedFields = Array.from(
      new Set(Object.values(this.requiredFields).reduce((acc, fields) => acc.concat(fields), []))
    );
    const requiredFields = this.requiredFields[gateway] || [];

    allManagedFields.forEach(name => {
      const control = this.locationConfigFG.get(name);
      if (!control) return;

      control.clearValidators();
      if (requiredFields.includes(name)) {
        control.setValidators(Validators.required);
      }
      if (!this.isEnabled(name)) {
        control.disable();
      }
      control.updateValueAndValidity();
    });
  }

  updateAmexOptions(gateway: Gateway) {
    // Payfac is only allowed if Gateway is Mars
    this.amexAccountTypes = Object.values(AmexAccountType).filter(type => {
      if (type === AmexAccountType.PayFac && gateway !== Gateway.Mars) {
        return false;
      }
      return !(type === AmexAccountType.MRPS);
    })
    // Reset Amex Account Type if current selection is invalid
    if (gateway !== Gateway.Mars && this.locationConfigFG.get('amexAccountType').value === AmexAccountType.PayFac) {
      this.locationConfigFG.get('amexAccountType').setValue(AmexAccountType.None);
    }
  }

  updateAmexValidators(type: AmexAccountType) {
    const ecomSECtrl = this.locationConfigFG.get('amexEcomSENumber');
    const f2fSECtrl = this.locationConfigFG.get('amexF2FSENumber');

    // Amex SE Number required only if type is "Direct"
    if (type === AmexAccountType.Direct) {
      ecomSECtrl.setValidators(Validators.required);
      f2fSECtrl.setValidators(Validators.required);
    } else {
      ecomSECtrl.clearValidators();
      f2fSECtrl.clearValidators();
    }
    ecomSECtrl.updateValueAndValidity();
    f2fSECtrl.updateValueAndValidity();
  }

  submit() {
    this.locationConfigFG.markAllAsTouched()
    if (this.locationConfigFG.invalid) {
      this.snackBar.open('Please fix the errors in the form before creating this merchant.', "close", {duration: 5000});
      return;
    }
    const newLocation = this.locationMapperService.toBackendModel(this.locationConfigFG.getRawValue());
    if (!newLocation.remoteTerminal || newLocation.remoteTerminal.id != this.locationConfigFG.get("remoteTerminalId").value)
    {
      newLocation.remoteTerminal = this.newRemoteTerminal(this.locationConfigFG.get("remoteTerminalId").value);
    }
    if (!newLocation.merchant || newLocation.merchant.id == undefined || newLocation.merchant.id == "")
    {
      newLocation.merchant.id = "rag-" + newLocation.location.id;
      newLocation.merchant.status = newLocation.location.status;
    }
    this.merchantService.create(newLocation)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (locationConfig) => {
          if (locationConfig) {
            this.router.navigate(['merchants', locationConfig.location.id])
            if (!this.snackBar._openedSnackBarRef) {
              this.snackBar.open('merchant has been created successfully', "close", {duration: 5000});
            }
          }
        },
        error: (e) => {
          console.error('error trying to create merchant.', e);
        },
      });
  }

  hasDomainNamesChanged(registeredDomainNames: string[], currentDomainNames: string[]): boolean {
    if (registeredDomainNames.length !== currentDomainNames.length) {
      return true;
    }

    const sortedRegistered = [...registeredDomainNames].sort();
    const sortedCurrent = [...currentDomainNames].sort();

    for (let i = 0; i < sortedRegistered.length; i++) {
      if (sortedRegistered[i] !== sortedCurrent[i]) {
        return true; // Found a mismatch
      }
    }

    return false;
  }

  update() {
    this.locationConfigFG.markAllAsTouched()
    if (this.locationConfigFG.invalid) {
      this.snackBar.open('Please fix the errors in the form before updating the merchant.', "close", {duration: 5000});
      return;
    }

    const mid: string = this.locationConfigFG.get('id')?.value;
    const updatedLocation = this.locationMapperService.toBackendModel(this.locationConfigFG.getRawValue());
    if (!updatedLocation.remoteTerminal || updatedLocation.remoteTerminal.id != this.locationConfigFG.get("remoteTerminalId").value)
    {
      updatedLocation.remoteTerminal = this.newRemoteTerminal(this.locationConfigFG.get("remoteTerminalId").value);
    }
    this.startLoading().pipe(
      switchMap((_) => { return this.merchantService.update(updatedLocation) }),
      switchMap((locationConfig): Observable<string[]> => {
        if(!this.hasApplePay) {
          this.handleUpdateSuccess(locationConfig);
          return EMPTY
        }
        return this.merchantService.getRegisteredDomainNamesByMID(mid).pipe(
          catchError(e => {
            console.log("error getting registered domain names:", e)
            return of([])
          })
        )
      }),
      switchMap((registeredDomainNames) => {
        if (!this.hasDomainNamesChanged(registeredDomainNames, this.domainNames)) {
          this.setDomainNames(registeredDomainNames)
          return EMPTY
        }
        return this.merchantService.addDomainNames(mid, this.domainNames)
      }),
      switchMap(() => this.merchantService.getByMID(mid)),
      switchMap((locationConfig) => {
        this.handleUpdateSuccess(locationConfig);
        return EMPTY
      }),
      finalize(() => {
        this.merchantService.getByMID(mid).pipe(
          switchMap((locationConfig) => {
            this.handleUpdateSuccess(locationConfig);
            return EMPTY
          }),
        ).subscribe()
        this.finishLoading()
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe()
  }

  updateAddDomainNames() {
    const mid: string = this.locationConfigFG.get('id')?.value || '';

    this.startLoading().pipe(
      switchMap((_) => this.merchantService.addDomainNames(mid, this.domainNames)),
      this.refreshRegisteredDomainNames(mid),
      finalize(() => {
        this.finishLoading()
        if (!this.snackBar._openedSnackBarRef) {
          this.snackBar.open('merchant has been updated successfully', "close", {duration: 5000})
        }
      }),
      takeUntilDestroyed(this.destroyRef),
    )
    .subscribe();
  }

  startLoading() {
    if (!this.isProcessing) {
      this.isProcessing = true;
      this.spinnerOverlayRef.attach(new ComponentPortal(MatProgressSpinner));
    }
    return of(true)
  }

  finishLoading() {
    if (this.isProcessing) {
      this.isProcessing = false;
      this.spinnerOverlayRef.detach();
    }
  }

  refreshRegisteredDomainNames(mid: string) {
    return pipe(
      switchMap(() => this.merchantService.getRegisteredDomainNamesByMID(mid)),
      catchError(e => {
        console.error('error trying to fetch registered domain names.', e);
        return EMPTY;
      }),
      tap(v => {
        this.setDomainNames(v);
      }),
    )
  }

  private handleUpdateSuccess(config?: LocationConfig) {
    if (config) {
      this.locationConfigFG = this.locationMapperService.createFormModel(config);
    }
    if (!this.snackBar._openedSnackBarRef) {
      this.snackBar.open('Merchant updated successfully', 'close', {duration: 2000});
    }
  }

  setDomainNames(names: string[]) {
    this.registeredDomainNames = names;
    this.domainNamesCtrl.setValue('');
    this.domainNamesCtrl.markAsPristine();
  }
  get domainNames(): string[] {
    return this.locationConfigFG.get('domainNames')?.value || [];
  }

  addDomainName(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    const control = this.locationConfigFG.get('domainNames');

    if (value && control) {
      const currentDomains = [...control.value]; // Create copy to ensure change detection
      currentDomains.push(value);
      // Update form value and mark as dirty to enable "Update" button
      control.setValue(currentDomains);
      control.markAsDirty();
      event.chipInput!.clear();
    }
    this.domainNamesCtrl.setValue('');
  }

  removeDomainName(domain: string): void {
    const control = this.locationConfigFG.get('domainNames');

    if (control) {
      const updatedDomains = control.value.filter((d: string) => d !== domain);
      control.setValue(updatedDomains);
      control.markAsDirty();
    }
  }

  isDomainRegistered(domainName: string): boolean {
    return this.registeredDomainNames?.indexOf(domainName) >= 0;
  }

  duplicateLocation(): void {
    const newLocation = this.locationMapperService.toBackendModel(this.locationConfigFG.getRawValue());

    // Sanitize the data
    newLocation.merchant.id = "";
    newLocation.remoteTerminal.id = "";
    newLocation.location.remoteTerminalId = "";
    newLocation.location.id = "";
    newLocation.location.status = Status.Active;
    newLocation.location.apiKeys = [];
    newLocation.location.gatewayUsername = "";
    newLocation.location.encryptedGatewayPassword = "";
    newLocation.location.createdBy = undefined;
    newLocation.location.createdDateTime = undefined;
    newLocation.location.lastModifiedBy = undefined;
    newLocation.location.lastModifiedDateTime = undefined;

    this.router.navigate(['/merchant'], {
      state: {
        data: newLocation,
        mode: 'create'
      }
    }).then(() => {
      window.scrollTo(0, 0);
    });
  }

  get apiKeys(): FormArray {
    return this.locationConfigFG.get('apiKeys') as FormArray;
  }

  addKey() {
    this.apiKeys.push(this.locationMapperService.createApiKeyGroup());
  }

  removeKey(index: number) {
    this.apiKeys.removeAt(index);
  }

  get applePayPPC(): LocationApplePayPaymentProcessingCertificate[] {
    return this.locationMapperService.applePayPPC;
  }

  get hasApplePay(): boolean {
    if (this.locationConfigFG?.get('gatewayName')?.value == Gateway.PayVector) {
      return false;
    }
    return this.locationConfigFG.get('isApplePayEnabled')?.value;
  }

  generateJWT(index: number) {
    const mid: string = this.locationConfigFG.get('id')?.value;
    const apiKeyId: string = this.apiKeys.at(index).get('key')?.value;
    this.merchantService.getJWTByApiKey(mid, apiKeyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v) => {
          navigator.clipboard.writeText(v.jwt)
            .then(() => {
              this.snackBar.open('JWT copied to buffer!', 'Close', {duration: 900});
            });
        },
        error: (e) => {
          console.error('error trying to fetch JWT for ApiKey.', e);
        }
      });
  }

  private newRemoteTerminal(terminalId: string): Terminal {
    let terminal = new Terminal();
    terminal.id = terminalId;
    terminal.status = "active";
    terminal.type = "remote";
    return terminal;
  }

  private filterMccCode(value: string): MccCode[]{
    if (!value) {
      value = "";
    }
    const lower = value.toLowerCase();
    return this.mccOptions.filter(code => code.mcc.toLowerCase().includes(lower) || code.text.toLowerCase().includes(lower));
  }

  get isDojo() {
    return this.locationConfigFG?.get('gatewayName')?.value == Gateway.Mars;
  }

  isEnabled(value: string): boolean {
    switch (value) {
      case 'update_merchant_domains':
        return [Role.Admin,Role.Maintainer,Role.ApplePayDomainAdder].includes(this.user?.Role)
      case 'update_merchant':
      case 'create_merchant':
      case 'duplicate_merchant':
        return [Role.Admin,Role.Maintainer].includes(this.user?.Role)
    }

    switch (this.user?.Role) {
      case Role.Admin:
        return true;
      case Role.Maintainer:
        return !["gatewayName","amexMid"].includes(value);
      case Role.ApplePayDomainAdder:
        return ["domainName","update_merchant_domains"].includes(value);
      default:
        return false;
    }
  }

  get isAdmin() {
    return this.user?.Role === Role.Admin;
  }

  get isMaintainer() {
    return this.user?.Role === Role.Maintainer;
  }

  get isApplePayDomainAdder() {
    return this.user?.Role === Role.ApplePayDomainAdder;
  }

  get isViewer() {
    return this.user?.Role === Role.Viewer;
  }

  get isCreateMode() {
    return this.mode == Mode.Create;
  }

  get isUpdateMode() {
    return this.mode == Mode.Update;
  }

  protected readonly Gateway = Gateway;
  protected readonly AmexAccountType = AmexAccountType;
}
